import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Inject, Input, NgModule } from '@angular/core';
import { TuiTiptapEditorService } from '@taiga-ui/addon-editor/directives';
import { TUI_EDITOR_OPTIONS, TUI_EDITOR_TOOLBAR_TEXTS } from '@taiga-ui/addon-editor/tokens';
import * as i3 from '@taiga-ui/cdk';
import { tuiDefaultProp, TuiActiveZoneModule, TuiLetModule } from '@taiga-ui/cdk';
import * as i6 from 'rxjs';
import { combineLatest } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import * as i1 from '@taiga-ui/core';
import { TuiHostedDropdownModule, TuiButtonModule, TuiHintModule, TuiDropdownModule } from '@taiga-ui/core';
import * as i2 from '@taiga-ui/addon-editor/components/color-selector';
import { TuiPaletteModule } from '@taiga-ui/addon-editor/components/color-selector';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@taiga-ui/addon-editor/abstract';

class TuiTableCellColorComponent {
    constructor(defaultOptions, editor, texts$) {
        this.defaultOptions = defaultOptions;
        this.editor = editor;
        this.texts$ = texts$;
        this.colors = this.defaultOptions.colors;
        this.colorText$ = this.texts$.pipe(map(texts => (this.editor.isActive('group') && texts.hiliteGroup) ||
            (this.editor.isActive('table') && texts.cellColor) ||
            ''));
        this.isActive$ = combineLatest([
            this.editor.isActive$('table'),
            this.editor.isActive$('group'),
        ]).pipe(map(([table, group]) => table || group));
        this.color$ = this.editor.stateChange$.pipe(map(() => this.editor.getCellColor() ||
            this.editor.getGroupColor() ||
            this.defaultOptions.blankColor), distinctUntilChanged());
    }
    isBlankColor(color) {
        return color === this.defaultOptions.blankColor;
    }
    setCellColor(color) {
        if (this.editor.isActive('group')) {
            this.editor.setGroupHilite(color);
        }
        else if (this.editor.isActive('table')) {
            this.editor.setCellColor(color);
        }
    }
}
TuiTableCellColorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTableCellColorComponent, deps: [{ token: TUI_EDITOR_OPTIONS }, { token: TuiTiptapEditorService }, { token: TUI_EDITOR_TOOLBAR_TEXTS }], target: i0.ɵɵFactoryTarget.Component });
TuiTableCellColorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiTableCellColorComponent, selector: "tui-table-cell-color", inputs: { colors: "colors" }, ngImport: i0, template: "<ng-container *tuiLet=\"color$ | async as color\">\n    <tui-hosted-dropdown\n        *tuiLet=\"isActive$ | async as isActive\"\n        #colorSelect\n        [content]=\"cellColorDropdown\"\n        [tuiDropdownMaxHeight]=\"999\"\n        [canOpen]=\"!!isActive\"\n    >\n        <button\n            tuiIconButton\n            type=\"button\"\n            size=\"s\"\n            tuiHintDirection=\"top-left\"\n            appearance=\"icon\"\n            icon=\"tuiIconPaintLarge\"\n            [tuiHint]=\"colorText$ | async\"\n            [tuiHintDescribe]=\"''\"\n            [pseudoActive]=\"colorSelect.open\"\n            [focusable]=\"colorSelect.open\"\n            [disabled]=\"!isActive\"\n        ></button>\n        <div\n            *ngIf=\"!isBlankColor(color || '')\"\n            class=\"t-plate t-plate_paint\"\n            [style.background]=\"color\"\n        ></div>\n        <ng-template\n            #cellColorDropdown\n            let-activeZone\n        >\n            <tui-palette\n                class=\"t-color-picker\"\n                [tuiActiveZoneParent]=\"activeZone\"\n                [colors]=\"colors\"\n                (selectedColor)=\"setCellColor($event); colorSelect.close()\"\n            ></tui-palette>\n        </ng-template>\n    </tui-hosted-dropdown>\n</ng-container>\n", styles: [".t-dropdown{padding:.75rem}.t-option{transition-property:background;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out}.t-option:hover{background:var(--tui-secondary-hover)}.t-option_margin{margin:0 .25rem}.t-plate{position:absolute;top:1.375rem;left:.25rem;width:1.5rem;height:.25rem;pointer-events:none}.t-plate_paint{left:.375rem;width:1.25rem}.t-color-picker{box-sizing:border-box;max-width:22.6rem}\n"], components: [{ type: i1.TuiHostedDropdownComponent, selector: "tui-hosted-dropdown", inputs: ["content", "sided", "canOpen", "open"], outputs: ["openChange", "focusedChange"] }, { type: i1.TuiButtonComponent, selector: "button[tuiButton], button[tuiIconButton], a[tuiButton], a[tuiIconButton]", inputs: ["appearance", "disabled", "icon", "iconRight", "shape", "showLoader", "size"] }, { type: i2.TuiPaletteComponent, selector: "tui-palette", inputs: ["colors"], outputs: ["selectedColor"] }], directives: [{ type: i3.TuiLetDirective, selector: "[tuiLet]", inputs: ["tuiLet"] }, { type: i1.TuiDropdownOptionsDirective, selector: "[tuiDropdownAlign], [tuiDropdownDirection], [tuiDropdownLimitWidth], [tuiDropdownMinHeight], [tuiDropdownMaxHeight], [tuiDropdownOffset]", inputs: ["tuiDropdownAlign", "tuiDropdownDirection", "tuiDropdownLimitWidth", "tuiDropdownMinHeight", "tuiDropdownMaxHeight", "tuiDropdownOffset"] }, { type: i1.TuiHintDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHint", "tuiHintContext", "tuiHintAppearance"] }, { type: i1.TuiHintDriverDirective, selector: "[tuiHint]" }, { type: i1.TuiHintHoverDirective, selector: "[tuiHint]:not(ng-container)", inputs: ["tuiHintShowDelay", "tuiHintHideDelay"], exportAs: ["tuiHintHover"] }, { type: i1.TuiHintPositionDirective, selector: "[tuiHint]:not([tuiHintCustomPosition])", inputs: ["tuiHintDirection"] }, { type: i1.TuiHintDescribeDirective, selector: "[tuiHintDescribe]", inputs: ["tuiHintDescribe"] }, { type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.TuiActiveZoneDirective, selector: "[tuiActiveZone]:not(ng-container), [tuiActiveZoneChange]:not(ng-container), [tuiActiveZoneParent]:not(ng-container)", inputs: ["tuiActiveZoneParent"], outputs: ["tuiActiveZoneChange"], exportAs: ["tuiActiveZone"] }], pipes: { "async": i4.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
__decorate([
    tuiDefaultProp()
], TuiTableCellColorComponent.prototype, "colors", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTableCellColorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-table-cell-color',
                    templateUrl: './table-cell-color.template.html',
                    styleUrls: ['../tools-common.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_OPTIONS]
                }] }, { type: i5.AbstractTuiEditor, decorators: [{
                    type: Inject,
                    args: [TuiTiptapEditorService]
                }] }, { type: i6.Observable, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_TOOLBAR_TEXTS]
                }] }]; }, propDecorators: { colors: [{
                type: Input
            }] } });

class TuiTableCellColorModule {
}
TuiTableCellColorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTableCellColorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiTableCellColorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTableCellColorModule, declarations: [TuiTableCellColorComponent], imports: [CommonModule,
        TuiHostedDropdownModule,
        TuiPaletteModule,
        TuiButtonModule,
        TuiHintModule,
        TuiActiveZoneModule,
        TuiDropdownModule,
        TuiLetModule], exports: [TuiTableCellColorComponent] });
TuiTableCellColorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTableCellColorModule, imports: [[
            CommonModule,
            TuiHostedDropdownModule,
            TuiPaletteModule,
            TuiButtonModule,
            TuiHintModule,
            TuiActiveZoneModule,
            TuiDropdownModule,
            TuiLetModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiTableCellColorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TuiHostedDropdownModule,
                        TuiPaletteModule,
                        TuiButtonModule,
                        TuiHintModule,
                        TuiActiveZoneModule,
                        TuiDropdownModule,
                        TuiLetModule,
                    ],
                    declarations: [TuiTableCellColorComponent],
                    exports: [TuiTableCellColorComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TuiTableCellColorComponent, TuiTableCellColorModule };
